import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

const EXIT_MS = 200;

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  footer?: ReactNode;

  side?: 'center' | 'left' | 'right';
}

const sizes = {
  sm: 'md:max-w-sm',
  md: 'md:max-w-md',
  lg: 'md:max-w-2xl',
  xl: 'md:max-w-4xl',
  '2xl': 'md:max-w-6xl',
};

export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
  footer,
  side = 'right',
}: ModalProps) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (mounted) {
      setClosing(true);
      const t = setTimeout(() => setMounted(false), EXIT_MS);
      return () => clearTimeout(t);
    }
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!mounted) return null;

  const isDrawer = side === 'left' || side === 'right';

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[100] flex bg-black/50',
        closing ? 'animate-fade-out' : 'animate-fade-in',
        'items-end justify-center',
        !isDrawer && 'md:items-center md:justify-center md:p-4',
        side === 'left' && 'md:items-stretch md:justify-start',
        side === 'right' && 'md:items-stretch md:justify-end',
      )}
      onClick={onClose}
    >
      <div
        className={cn(
          'flex w-full flex-col border border-border bg-surface-elevated shadow-card-hover',
          'max-h-[88vh] rounded-t-2xl',
          closing ? 'animate-slide-down' : 'animate-slide-up',
          !isDrawer && 'md:max-h-[calc(100vh-2rem)] md:rounded-2xl',
          !isDrawer && (closing ? 'md:animate-slide-down' : 'md:animate-slide-up'),
          isDrawer && 'md:h-full md:max-h-screen',
          side === 'left' &&
            'md:rounded-l-none md:rounded-tr-[1.25rem] md:rounded-br-[1.25rem] md:border-l-0',
          side === 'left' && (closing ? 'md:animate-slide-out-left' : 'md:animate-slide-in-left'),
          side === 'right' &&
            'md:rounded-r-none md:rounded-tl-[1.25rem] md:rounded-bl-[1.25rem] md:border-r-0',
          side === 'right' &&
            (closing ? 'md:animate-slide-out-right' : 'md:animate-slide-in-right'),
          'max-w-full',
          sizes[size],
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-border md:hidden" />
        {title && (
          <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-3.5 md:px-6 md:py-4">
            <h2 className="text-lg font-semibold text-content">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1.5 text-content-muted hover:bg-surface-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto p-5 md:p-6">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-border px-5 py-3.5 md:px-6 md:py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
